'use client';

import Image from 'next/image';
import { format } from 'date-fns';
import { ar as arDateLocale } from 'date-fns/locale';
import { useState, useMemo, type ReactNode } from 'react';
import {
  ArrowRight,
  Check,
  X,
  Bot,
  MessageSquare,
  Zap,
  Clock,
  DollarSign,
  Users,
  Shield,
  Globe,
  TrendingUp,
  AlertTriangle,
  ChevronRight,
  Award,
  Target,
  Sparkles,
  Brain,
  HeadphonesIcon,
  BarChart3,
  ThumbsUp,
  ThumbsDown,
  Calculator,
  AlertCircle,
  Star,
  StarHalf,
  Minus,
  Plus,
  ExternalLink,
  Quote,
  Server,
  Settings,
  Layers,
  CircleDollarSign,
  Timer,
  XCircle,
  CheckCircle,
  HelpCircle,
  TrendingDown,
} from 'lucide-react';

import { Link } from '@/lib/i18n/link';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { cn } from '@/lib/utils';
import { Slider } from '@/components/ui/slider';
import { useLocale } from '@/lib/i18n/context';

import { AR } from './ar';
import { BLOG_AR_ENABLED } from '@/lib/blog/ar-enabled';
import { ORGANIZATION_ID } from '@/lib/metadata-helper';

const BASE_URL = 'https://www.open.cx';

const JSON_LD_HEADLINE = 'Zendesk AI Agents: The Complete Review (2026)';
const JSON_LD_DESCRIPTION =
  'An honest, in-depth review of Zendesk AI agents. Pricing analysis, real customer complaints, performance benchmarks, and better alternatives.';
const JSON_LD_REVIEW_BODY =
  'Zendesk AI agents offer enterprise-grade features but at premium prices. Implementation complexity, hallucination issues, and difficult human escalation make it a challenging choice for most businesses.';
const JSON_LD_FAQS = [
  {
    question: 'How much does Zendesk AI agents cost?',
    answer:
      'Zendesk AI agents cost $1.50 per automated resolution, plus base platform fees of $55-150/agent/month, plus AI add-ons of $50-100/agent/month. Total costs can exceed $20,000/month for mid-sized teams.',
  },
  {
    question: 'Is Zendesk AI worth it?',
    answer:
      'For large enterprises already deeply invested in Zendesk, maybe. For most businesses, alternatives like Open or Intercom Fin offer better value with simpler setup, lower per-resolution costs, and comparable or better AI performance.',
  },
];

/** Picks the Arabic value when we are on `/ar` and it exists, else English. */
function usePick() {
  const locale = useLocale();
  const isAr = locale === 'ar' && BLOG_AR_ENABLED;
  return {
    isAr,
    pick: function pick<T>(ar: T | undefined, en: T): T {
      return isAr && ar !== undefined ? ar : en;
    },
  };
}

// The Good Things
const goodPoints = [
  {
    title: 'Omnichannel Coverage',
    description:
      'Zendesk AI agents work across email, chat, social media, WhatsApp, and even voice (with Voice AI). One AI handles all channels consistently.',
    icon: Globe,
  },
  {
    title: 'Deep Zendesk Integration',
    description:
      "If you're already on Zendesk, the AI agents integrate seamlessly with your existing tickets, macros, help center, and workflows. No rip-and-replace needed.",
    icon: Layers,
  },
  {
    title: 'Enterprise-Grade Security',
    description:
      'SOC 2 compliance, GDPR, HIPAA options. Data residency controls. Enterprise customers get the security checkboxes they need.',
    icon: Shield,
  },
  {
    title: 'Quality Assurance Tools',
    description:
      'Zendesk QA and Automated Quality Scores let you monitor AI performance, compare against human agents, and catch issues before customers do.',
    icon: BarChart3,
  },
];

// The Bad Things (Main Focus)
const badPoints = [
  {
    title: 'Eye-Watering Pricing',
    description:
      'At $1.50 per automated resolution (plus $55-150/agent/month base), costs spiral fast. A company with 10,000 monthly resolutions pays $15,000/month just for AI, on top of seat costs.',
    severity: 'high',
    icon: CircleDollarSign,
    details: [
      'Base plans start at $55/agent/month (Team) up to $150+/agent/month (Enterprise)',
      'AI add-ons: $50-100/agent/month extra',
      'Per-resolution pricing: $1.50 per automated resolution',
      'Competitors like Intercom charge $0.99 per resolution, 33% cheaper',
    ],
  },
  {
    title: 'Implementation Nightmare',
    description:
      'Despite "zero-training" marketing claims, real implementation requires dedicated teams, months of setup, and ongoing tuning. 62% of CX leaders report decision paralysis around Zendesk AI adoption.',
    severity: 'high',
    icon: Settings,
    details: [
      'Requires project lead, AI builders, CRM admin, and integration engineers',
      'Help center content must be optimized before AI works well',
      'Custom integrations turn into "months-long problems"',
      "Non-deterministic AI systems don't fit classic development timelines",
    ],
  },
  {
    title: 'Hallucination & Wrong Answers',
    description:
      "Zendesk's own community forums are filled with complaints about AI confidently providing incorrect information, broken links, and outdated contact pages.",
    severity: 'high',
    icon: AlertCircle,
    details: [
      'AI directs customers to non-existent support pages',
      'Multi-turn conversations fail when customers clarify or interrupt',
      'Ambiguous questions trigger confident but wrong responses',
      "Grounding in knowledge base helps but doesn't eliminate the problem",
    ],
  },
  {
    title: 'Human Escalation Black Hole',
    description:
      "When AI can't help (which happens often), customers report extreme difficulty reaching human agents. The AI becomes a wall, not a helper.",
    severity: 'high',
    icon: XCircle,
    details: [
      '"They never get you where you want to go" (common user complaint)',
      "AI can't handle account-specific debugging but won't transfer properly",
      'Finding alternative contact methods is deliberately difficult',
      'Customers forced to "start over" when finally reaching humans',
    ],
  },
  {
    title: 'Resolution Rate Reality Check',
    description:
      'Zendesk claims "80% automation" but independent testing tells a different story. Intercom\'s Fin outperformed Zendesk AI in 80% of test cases.',
    severity: 'medium',
    icon: TrendingDown,
    details: [
      'Marketing claims of 80% automation vs. real-world 40-60%',
      'Intercom Fin handles "twice as many complex questions"',
      '"Understood conversations" metric inflates perceived performance',
      "Single-turn accuracy doesn't translate to multi-turn success",
    ],
  },
  {
    title: 'Platform Complexity & Bloat',
    description:
      'Zendesk has become a sprawling platform requiring third-party apps for basic functionality. The interface feels dated compared to modern alternatives.',
    severity: 'medium',
    icon: Server,
    details: [
      'Fragmented experience requiring marketplace apps',
      'Limited native Slack/Teams integration',
      "Interface hasn't kept pace with modern design standards",
      'Feature bloat makes simple tasks complicated',
    ],
  },
];

// Customer Complaints (Real Quotes/Paraphrased)
const customerComplaints = [
  {
    quote:
      'AI directed me to a retired Zendesk contact page that no longer existed. I was stuck in a loop with no way to reach support.',
    source: 'Gergely Orosz, LinkedIn',
    context: 'Paying Zendesk customer',
    rating: 1,
  },
  {
    quote:
      'They never get you where you want to go and they are more of an annoyance and a hindrance. I would much rather speak to a real, live person.',
    source: 'Industry survey respondent',
    context: 'On AI chatbot experiences',
    rating: 2,
  },
  {
    quote:
      'The bot recites irrelevant FAQ content, repeats itself, and fails to understand context. Pushing me back to square one every time.',
    source: 'Reddit user',
    context: 'Customer support discussion',
    rating: 1,
  },
];

// Pricing Comparison
const pricingComparison = [
  {
    provider: 'Zendesk AI',
    baseCost: '$55-150/agent/mo',
    aiCost: '$1.50/resolution',
    notes: 'Plus $50-100/agent AI add-on',
    highlight: false,
  },
  {
    provider: 'Intercom Fin',
    baseCost: 'Varies',
    aiCost: '$0.90/resolution',
    notes: '33% cheaper per resolution',
    highlight: false,
  },
  {
    provider: 'Open',
    baseCost: 'Included',
    aiCost: '$0.90/resolution',
    notes: 'No seat fees, only pay for results',
    highlight: true,
  },
  {
    provider: 'Freshdesk',
    baseCost: 'Free-$79/agent/mo',
    aiCost: 'Included in plans',
    notes: 'Lower cost alternative',
    highlight: false,
  },
];

// Feature Comparison
const featureComparison = [
  {
    feature: 'Per-Resolution Pricing',
    zendesk: true,
    open: true,
    intercom: true,
    freshdesk: false,
  },
  {
    feature: 'No Per-Seat Fees',
    zendesk: false,
    open: true,
    intercom: false,
    freshdesk: false,
  },
  {
    feature: 'True LLM Understanding',
    zendesk: true,
    open: true,
    intercom: true,
    freshdesk: 'partial',
  },
  {
    feature: 'Easy Setup (<1 day)',
    zendesk: false,
    open: true,
    intercom: 'partial',
    freshdesk: 'partial',
  },
  {
    feature: 'Omnichannel Support',
    zendesk: true,
    open: true,
    intercom: true,
    freshdesk: true,
  },
  {
    feature: 'Seamless Human Handoff',
    zendesk: 'partial',
    open: true,
    intercom: true,
    freshdesk: 'partial',
  },
  {
    feature: 'Voice AI',
    zendesk: true,
    open: true,
    intercom: false,
    freshdesk: false,
  },
  {
    feature: 'Works with Existing Helpdesk',
    zendesk: 'N/A',
    open: true,
    intercom: 'partial',
    freshdesk: 'N/A',
  },
];

// Service Incidents
const serviceIncidents = [
  {
    date: 'June 1-5, 2025',
    issue: 'AI Agents (uGPT) response latency',
    impact: '15% of conversations affected',
    duration: '4+ days',
    cause: 'Servers ran out of available network IP addresses',
  },
  {
    date: 'April 23, 2025',
    issue: 'Ticket update delays & inaccurate counts',
    impact: 'Pod 17 customers',
    duration: '~2 hours',
    cause: 'Premature traffic redirect during infrastructure update',
  },
];

const FAQS: {
  value: string;
  question: string;
  answer?: string;
  answerNode?: ReactNode;
}[] = [
  {
    value: 'faq-1',
    question: 'How much do Zendesk AI agents really cost?',
    answer:
      "The total cost includes: base platform ($55-150/agent/month), AI add-ons ($50-100/agent/month), and per-resolution fees ($1.50/resolution). A team of 10 agents with 5,000 monthly AI resolutions would pay roughly $9,250/month. That's $111,000/year before any overage fees.",
  },
  {
    value: 'faq-2',
    question: 'Is Zendesk AI better than Intercom Fin?',
    answer:
      "Independent testing shows Intercom Fin outperforms Zendesk AI in 80% of test cases and handles twice as many complex questions. Fin also costs less ($0.99/resolution vs $1.50). Unless you're deeply integrated with Zendesk already, Fin is typically the better choice.",
  },
  {
    value: 'faq-3',
    question: 'Can Zendesk AI agents hallucinate?',
    answer:
      "Yes. Like all LLM-based systems, Zendesk AI can confidently provide incorrect information. Users report being directed to non-existent pages, receiving wrong policy information, and getting stuck in loops. Zendesk's QA tools help catch issues, but don't eliminate them.",
  },
  {
    value: 'faq-4',
    question: 'How long does Zendesk AI implementation take?',
    answer:
      'Despite "zero-training" marketing, real implementation typically takes weeks to months. You need to optimize help center content, configure integrations, set up business procedures, and tune the AI. Zendesk recommends assembling a dedicated team including a project lead, AI builders, and CRM admin.',
  },
  {
    value: 'faq-5',
    question: "What's the best Zendesk AI alternative?",
    answerNode: (
      <>
        For pure AI performance: <strong>Intercom Fin</strong>. For value
        (performance + price): <strong>Open</strong>, which offers
        $0.99/resolution with no seat fees, works with your existing helpdesk,
        and achieves 77% automation. For budget-conscious teams:{' '}
        <strong>Freshdesk</strong> offers a free tier with basic AI.
      </>
    ),
  },
  {
    value: 'faq-6',
    question: 'Should I switch from Zendesk?',
    answer:
      "It depends on your situation. If you're an enterprise with deep Zendesk integration, switching costs may outweigh benefits. But if you're on the fence, paying too much, or frustrated with AI performance, alternatives like Open can layer on top of Zendesk (no rip-and-replace needed) to give you better AI at lower cost.",
  },
];

// Generate last updated date
const lastUpdated = new Date();
lastUpdated.setDate(lastUpdated.getDate() - 1);

// Cost Calculator Component
function CostCalculator() {
  const { pick } = usePick();
  const [monthlyResolutions, setMonthlyResolutions] = useState(5000);
  const [agentCount, setAgentCount] = useState(10);

  const costs = useMemo(() => {
    const zendeskBase = agentCount * 100; // Mid-tier estimate
    const zendeskAIAddon = agentCount * 75; // Mid-tier AI add-on
    const zendeskResolutions = monthlyResolutions * 1.5;
    const zendeskTotal = zendeskBase + zendeskAIAddon + zendeskResolutions;

    const openResolutions = monthlyResolutions * 0.99;
    const openTotal = openResolutions; // No seat fees

    const intercomBase = agentCount * 65; // Estimate
    const intercomResolutions = monthlyResolutions * 0.99;
    const intercomTotal = intercomBase + intercomResolutions;

    return {
      zendesk: {
        base: zendeskBase,
        aiAddon: zendeskAIAddon,
        resolutions: zendeskResolutions,
        total: zendeskTotal,
      },
      open: {
        resolutions: openResolutions,
        total: openTotal,
      },
      intercom: {
        base: intercomBase,
        resolutions: intercomResolutions,
        total: intercomTotal,
      },
      savings: zendeskTotal - openTotal,
      savingsPercent: Math.round(
        ((zendeskTotal - openTotal) / zendeskTotal) * 100,
      ),
    };
  }, [monthlyResolutions, agentCount]);

  return (
    <div className="rounded-2xl border-2 border-neutral-200 bg-white p-6 md:p-8">
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-orange-100">
          <Calculator className="h-6 w-6 text-orange-600" />
        </div>
        <div>
          <h3 className="text-xl text-neutral-900">
            {pick(AR.calcTitle, 'Zendesk AI Cost Calculator')}
          </h3>
          <p className="text-sm text-neutral-500">
            {pick(AR.calcSubtitle, "See what you'd actually pay")}
          </p>
        </div>
      </div>

      <div className="mb-8 space-y-6">
        <div>
          <div className="mb-2 flex items-center justify-between">
            <label className="text-sm font-medium text-neutral-700">
              {pick(AR.calcResolutionsLabel, 'Monthly AI Resolutions')}
            </label>
            <span className="rounded-full bg-neutral-100 px-3 py-1 text-xs font-medium uppercase tracking-wide text-neutral-600">
              {monthlyResolutions.toLocaleString()}
            </span>
          </div>
          <Slider
            value={[monthlyResolutions]}
            onValueChange={(v) => setMonthlyResolutions(v[0])}
            min={1000}
            max={50000}
            step={1000}
            className="w-full"
          />
          <div className="mt-1 flex justify-between text-xs text-neutral-400">
            <span>1,000</span>
            <span>50,000</span>
          </div>
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <label className="text-sm font-medium text-neutral-700">
              {pick(AR.calcTeamSizeLabel, 'Support Team Size')}
            </label>
            <span className="rounded-full bg-neutral-100 px-3 py-1 text-sm font-semibold text-neutral-900">
              {agentCount} {pick(AR.calcAgentsSuffix, 'agents')}
            </span>
          </div>
          <Slider
            value={[agentCount]}
            onValueChange={(v) => setAgentCount(v[0])}
            min={1}
            max={100}
            step={1}
            className="w-full"
          />
          <div className="mt-1 flex justify-between text-xs text-neutral-400">
            <span>1</span>
            <span>100</span>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <div className="rounded-xl border-2 border-red-200 bg-red-50 p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-red-900">
                {pick(AR.calcZendeskLabel, 'Zendesk AI (Full Stack)')}
              </div>
              <div className="text-xs text-red-700">
                ${costs.zendesk.base.toLocaleString()}{' '}
                {pick(AR.calcBaseWord, 'base')} + $
                {costs.zendesk.aiAddon.toLocaleString()}{' '}
                {pick(AR.calcAiWord, 'AI')} + $
                {costs.zendesk.resolutions.toLocaleString()}{' '}
                {pick(AR.calcResolutionsWord, 'resolutions')}
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-red-900">
                ${costs.zendesk.total.toLocaleString()}
              </div>
              <div className="text-xs text-red-700">
                {pick(AR.calcPerMonth, '/month')}
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-xl border-2 border-amber-200 bg-amber-50 p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-amber-900">
                {pick(AR.calcIntercomLabel, 'Intercom + Fin')}
              </div>
              <div className="text-xs text-amber-700">
                ${costs.intercom.base.toLocaleString()}{' '}
                {pick(AR.calcBaseWord, 'base')} + $
                {costs.intercom.resolutions.toLocaleString()}{' '}
                {pick(AR.calcResolutionsWord, 'resolutions')}
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-amber-900">
                ${costs.intercom.total.toLocaleString()}
              </div>
              <div className="text-xs text-amber-700">
                {pick(AR.calcPerMonth, '/month')}
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-xl border-2 border-green-300 bg-green-50 p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-green-900">
                {pick(AR.calcOpenLabel, 'Open (No Seat Fees)')}
              </div>
              <div className="text-xs text-green-700">
                ${costs.open.resolutions.toLocaleString()}{' '}
                {pick(AR.calcResolutionsOnly, 'resolutions only')}
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-green-900">
                ${costs.open.total.toLocaleString()}
              </div>
              <div className="text-xs text-green-700">
                {pick(AR.calcPerMonth, '/month')}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 rounded-xl bg-neutral-900 p-4 text-center">
          <div className="text-sm text-neutral-400">
            {pick(AR.calcSavingsLabel, "You'd save with Open")}
          </div>
          <div className="text-3xl font-bold text-white">
            ${costs.savings.toLocaleString()}
            {pick(AR.calcSavingsPerMonth, '/mo')}
          </div>
          <div className="text-sm text-green-400">
            {costs.savingsPercent}%{' '}
            {pick(AR.calcSavingsPercentSuffix, 'less than Zendesk')}
          </div>
        </div>
      </div>
    </div>
  );
}

// Rating Stars Component
function RatingStars({ rating }: { rating: number }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={cn(
            'h-4 w-4',
            star <= rating
              ? 'fill-amber-400 text-amber-400'
              : 'text-neutral-300',
          )}
        />
      ))}
    </div>
  );
}

// Feature Cell Component
function FeatureCell({ value }: { value: boolean | string }) {
  if (value === true) {
    return <CheckCircle className="h-5 w-5 text-green-500" />;
  }
  if (value === false) {
    return <XCircle className="h-5 w-5 text-red-400" />;
  }
  if (value === 'partial') {
    return <HelpCircle className="h-5 w-5 text-amber-500" />;
  }
  return <span className="text-xs text-neutral-400">N/A</span>;
}

export default function ZendeskAIAgentsReview() {
  const { isAr, pick } = usePick();
  const [expandedBadPoint, setExpandedBadPoint] = useState<string | null>(null);

  const localizedGoodPoints = goodPoints.map((point, idx) => {
    const a = isAr ? AR.goodPoints?.[idx] : undefined;
    return {
      ...point,
      title: a?.title ?? point.title,
      description: a?.description ?? point.description,
    };
  });

  const localizedBadPoints = badPoints.map((point, idx) => {
    const a = isAr ? AR.badPoints?.[idx] : undefined;
    return {
      ...point,
      title: a?.title ?? point.title,
      description: a?.description ?? point.description,
      details: a?.details ?? point.details,
    };
  });

  const localizedComplaints = customerComplaints.map((complaint, idx) => {
    const a = isAr ? AR.customerComplaints?.[idx] : undefined;
    return {
      ...complaint,
      quote: a?.quote ?? complaint.quote,
      source: a?.source ?? complaint.source,
      context: a?.context ?? complaint.context,
    };
  });

  const localizedPricing = pricingComparison.map((row, idx) => {
    const a = isAr ? AR.pricingComparison?.[idx] : undefined;
    return {
      ...row,
      provider: a?.provider ?? row.provider,
      baseCost: a?.baseCost ?? row.baseCost,
      aiCost: a?.aiCost ?? row.aiCost,
      notes: a?.notes ?? row.notes,
    };
  });

  const localizedFeatures = featureComparison.map((row, idx) => ({
    ...row,
    feature: (isAr ? AR.featureRows?.[idx] : undefined) ?? row.feature,
  }));

  const localizedIncidents = serviceIncidents.map((incident, idx) => {
    const a = isAr ? AR.serviceIncidents?.[idx] : undefined;
    return {
      ...incident,
      date: a?.date ?? incident.date,
      issue: a?.issue ?? incident.issue,
      impact: a?.impact ?? incident.impact,
      duration: a?.duration ?? incident.duration,
    };
  });

  const localizedFaqs = FAQS.map((faq, idx) => {
    const a = isAr ? AR.faqs?.[idx] : undefined;
    return {
      ...faq,
      question: a?.question ?? faq.question,
      answer: a?.answer ?? faq.answer,
      answerNode: a?.answer ? undefined : faq.answerNode,
    };
  });

  const localizedJsonLdFaqs = JSON_LD_FAQS.map((faq, idx) => {
    const a = isAr ? AR.jsonLdFaqs?.[idx] : undefined;
    return {
      question: a?.question ?? faq.question,
      answer: a?.answer ?? faq.answer,
    };
  });

  const updatedDate = isAr
    ? format(lastUpdated, 'd MMMM yyyy', { locale: arDateLocale })
    : format(lastUpdated, 'MMMM d, yyyy');

  return (
    <div className="bg-white">
      {/* JSON-LD Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify([
            {
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: pick(AR.jsonLdHeadline, JSON_LD_HEADLINE),
              description: pick(AR.jsonLdDescription, JSON_LD_DESCRIPTION),
              url: `${BASE_URL}/blog/zendesk-ai-agents-review`,
              datePublished: '2026-01-30T00:00:00Z',
              dateModified: format(lastUpdated, "yyyy-MM-dd'T'HH:mm:ss'Z'"),
              author: { '@id': ORGANIZATION_ID },
              publisher: {
                '@type': 'Organization',
                name: 'Open',
                url: BASE_URL,
                logo: { '@type': 'ImageObject', url: `${BASE_URL}/logo.png` },
              },
            },
            {
              '@context': 'https://schema.org',
              '@type': 'Review',
              itemReviewed: {
                '@type': 'SoftwareApplication',
                name: 'Zendesk AI Agents',
                applicationCategory: 'Customer Service Software',
              },
              reviewRating: {
                '@type': 'Rating',
                ratingValue: '2.5',
                bestRating: '5',
                worstRating: '1',
              },
              author: { '@type': 'Organization', name: 'Open' },
              reviewBody: pick(AR.jsonLdReviewBody, JSON_LD_REVIEW_BODY),
            },
            {
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: localizedJsonLdFaqs.map((faq) => ({
                '@type': 'Question',
                name: faq.question,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: faq.answer,
                },
              })),
            },
          ]),
        }}
      />

      {/* Hero */}
      <header className="relative overflow-hidden border-b border-neutral-100 pt-32 pb-16">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-white to-red-50" />
        <div className="container relative">
          <div className="mx-auto max-w-4xl">
            {/* Breadcrumb */}
            <nav className="mb-6 text-sm text-neutral-500">
              <Link href="/" className="hover:text-neutral-900">
                {pick(AR.breadcrumbHome, 'Home')}
              </Link>
              <ChevronRight className="mx-2 inline h-4 w-4" />
              <Link href="/blog" className="hover:text-neutral-900">
                {pick(AR.breadcrumbResources, 'Resources')}
              </Link>
              <ChevronRight className="mx-2 inline h-4 w-4" />
              <span className="text-neutral-900">
                {pick(AR.breadcrumbCurrent, 'Zendesk AI Review')}
              </span>
            </nav>

            {/* Badge */}
            <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-orange-100 px-4 py-1.5 text-sm font-medium text-orange-700">
              <AlertTriangle className="h-4 w-4" />
              {pick(AR.badge, 'Review')}
            </div>

            {/* Title */}
            <h1 className="mb-6 font-serif text-4xl md:text-5xl lg:text-6xl">
              {pick(
                AR.title,
                'Zendesk AI Agents review: features, pricing, and gaps',
              )}
            </h1>

            {/* Subtitle */}
            <p className="mb-8 max-w-3xl text-xl leading-relaxed text-neutral-600">
              {pick(
                AR.subtitle,
                "The good, the bad, and the expensive. An honest look at Zendesk's AI agents: what they do well, where they fail, and whether they're worth the premium price tag.",
              )}
            </p>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-neutral-500">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 overflow-hidden rounded-full bg-neutral-100">
                  <Image
                    src="/images/about/mo.jpg"
                    alt="Author"
                    width={32}
                    height={32}
                    className="h-full w-full object-cover"
                  />
                </div>
                <span>{pick(AR.byline, 'By the Open Team')}</span>
              </div>
              <span className="text-neutral-300">|</span>
              <span>
                {pick(AR.updatedLabel, 'Updated')} {updatedDate}
              </span>
              <span className="text-neutral-300">|</span>
              <span>{pick(AR.readTime, '18 min read')}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Quick Verdict Stats */}
      <div className="bg-neutral-900 py-8">
        <div className="container">
          <div className="grid gap-8 text-center sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <div className="mb-1 text-3xl font-bold text-white">2.5/5</div>
              <div className="text-sm text-neutral-400">
                {pick(AR.statRatingLabel, 'Our Rating')}
              </div>
            </div>
            <div>
              <div className="mb-1 text-3xl font-bold text-red-400">$1.50</div>
              <div className="text-sm text-neutral-400">
                {pick(AR.statPerResolutionLabel, 'Per Resolution (Expensive)')}
              </div>
            </div>
            <div>
              <div className="mb-1 text-3xl font-bold text-amber-400">1.8★</div>
              <div className="text-sm text-neutral-400">
                {pick(AR.statTrustpilotLabel, 'Trustpilot Rating')}
              </div>
            </div>
            <div>
              <div className="mb-1 text-3xl font-bold text-white">62%</div>
              <div className="text-sm text-neutral-400">
                {pick(
                  AR.statParalysisLabel,
                  'Leaders Report Decision Paralysis',
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-16">
        <div className="container">
          <div className="mx-auto max-w-4xl">
            {/* Table of Contents */}
            <nav className="mb-16 rounded-xl border border-neutral-200 bg-neutral-50 p-6">
              <h2 className="mb-4 text-neutral-900">
                {pick(AR.tocHeading, 'In This Review')}
              </h2>
              <div className="grid gap-2 sm:grid-cols-2">
                <a
                  href="#tldr"
                  className="flex items-center gap-2 text-sm text-neutral-600 hover:text-neutral-900"
                >
                  <ChevronRight className="h-4 w-4" />
                  {pick(AR.tocTldr, 'TL;DR Summary')}
                </a>
                <a
                  href="#what-is"
                  className="flex items-center gap-2 text-sm text-neutral-600 hover:text-neutral-900"
                >
                  <ChevronRight className="h-4 w-4" />
                  {pick(AR.tocWhatIs, 'What Are Zendesk AI Agents?')}
                </a>
                <a
                  href="#the-good"
                  className="flex items-center gap-2 text-sm text-neutral-600 hover:text-neutral-900"
                >
                  <ChevronRight className="h-4 w-4" />
                  {pick(AR.tocGood, 'The Good (Brief)')}
                </a>
                <a
                  href="#the-bad"
                  className="flex items-center gap-2 text-sm text-neutral-600 hover:text-neutral-900"
                >
                  <ChevronRight className="h-4 w-4" />
                  {pick(AR.tocBad, 'The Bad (In-Depth)')}
                </a>
                <a
                  href="#calculator"
                  className="flex items-center gap-2 text-sm text-neutral-600 hover:text-neutral-900"
                >
                  <ChevronRight className="h-4 w-4" />
                  {pick(AR.tocCalculator, 'Cost Calculator')}
                </a>
                <a
                  href="#complaints"
                  className="flex items-center gap-2 text-sm text-neutral-600 hover:text-neutral-900"
                >
                  <ChevronRight className="h-4 w-4" />
                  {pick(AR.tocComplaints, 'Real Customer Complaints')}
                </a>
                <a
                  href="#comparison"
                  className="flex items-center gap-2 text-sm text-neutral-600 hover:text-neutral-900"
                >
                  <ChevronRight className="h-4 w-4" />
                  {pick(AR.tocComparison, 'Feature Comparison')}
                </a>
                <a
                  href="#verdict"
                  className="flex items-center gap-2 text-sm text-neutral-600 hover:text-neutral-900"
                >
                  <ChevronRight className="h-4 w-4" />
                  {pick(AR.tocVerdict, 'Final Verdict')}
                </a>
              </div>
            </nav>

            {/* TL;DR */}
            <section id="tldr" className="mb-16">
              <div className="rounded-2xl border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50 p-6 md:p-8">
                <h2 className="mb-4 flex items-center gap-2 text-xl text-neutral-900">
                  <Zap className="h-5 w-5 text-orange-500" />
                  {pick(AR.tldrHeading, 'TL;DR: Skip this if you want details')}
                </h2>
                <div className="space-y-4 text-neutral-700">
                  <p>
                    {pick(
                      AR.tldrP1,
                      <>
                        <strong>
                          Zendesk AI agents are powerful but problematic.
                        </strong>{' '}
                        They offer enterprise-grade features, deep Zendesk
                        integration, and omnichannel support, but at a cost
                        that's hard to justify.
                      </>,
                    )}
                  </p>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="rounded-lg bg-green-100 p-4">
                      <div className="mb-2 flex items-center gap-2 font-semibold text-green-800">
                        <ThumbsUp className="h-4 w-4" />
                        {pick(AR.tldrGoodForTitle, 'Good For')}
                      </div>
                      <ul className="space-y-1 text-sm text-green-700">
                        {pick(AR.tldrGoodForItems, [
                          '• Large enterprises already on Zendesk',
                          '• Teams with dedicated implementation resources',
                          '• Companies needing strict compliance',
                        ]).map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="rounded-lg bg-red-100 p-4">
                      <div className="mb-2 flex items-center gap-2 font-semibold text-red-800">
                        <ThumbsDown className="h-4 w-4" />
                        {pick(AR.tldrBadForTitle, 'Bad For')}
                      </div>
                      <ul className="space-y-1 text-sm text-red-700">
                        {pick(AR.tldrBadForItems, [
                          '• Budget-conscious teams (costs add up fast)',
                          '• Fast-moving startups (implementation is slow)',
                          '• Anyone wanting simple, reliable AI',
                        ]).map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <p className="text-sm">
                    {pick(
                      AR.tldrBottomLine,
                      <>
                        <strong>Bottom line:</strong> At $1.50/resolution + high
                        seat costs, Zendesk AI agents are 50%+ more expensive
                        than alternatives like Open ($0.99/resolution, no seats)
                        or Intercom Fin ($0.99/resolution), often with worse
                        real-world performance.
                      </>,
                    )}
                  </p>
                </div>
              </div>
            </section>

            {/* What Are Zendesk AI Agents */}
            <section id="what-is" className="mb-16">
              <h2 className="mb-6 font-serif text-3xl">
                {pick(AR.whatIsHeading, 'What Are Zendesk AI Agents?')}
              </h2>

              <div className="prose prose-lg prose-neutral mb-8 max-w-none prose-headings:font-serif prose-headings:scroll-mt-28 prose-p:text-neutral-700 prose-p:leading-relaxed prose-a:text-neutral-900 prose-a:underline-offset-2 prose-strong:text-neutral-900 prose-li:text-neutral-700">
                <p>
                  {pick(
                    AR.whatIsP1,
                    'Zendesk AI agents are the company\'s answer to the generative AI revolution. Built on what Zendesk calls "agentic AI" technology, they\'re designed to autonomously handle customer inquiries across all channels: chat, email, social, WhatsApp, and even voice.',
                  )}
                </p>
                <p>
                  {pick(
                    AR.whatIsP2,
                    'Unlike traditional rule-based chatbots, Zendesk AI agents can (theoretically) reason through complex problems, maintain conversation context, and take real actions like processing refunds or updating accounts.',
                  )}
                </p>
              </div>

              <div className="mb-8 grid gap-4 sm:grid-cols-2">
                <div className="rounded-xl border border-neutral-200 bg-white p-5">
                  <h3 className="mb-3 text-neutral-900">
                    {pick(AR.claimsTitle, 'What Zendesk Claims')}
                  </h3>
                  <ul className="space-y-2 text-sm text-neutral-600">
                    {pick(AR.claimsItems, [
                      'Resolve 80%+ of customer interactions',
                      'Deploy in minutes with zero training',
                      'Outcome-based pricing (pay for results)',
                      'Agentic reasoning for complex issues',
                    ]).map((item) => (
                      <li key={item} className="flex items-start gap-2">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-green-500" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-xl border border-red-200 bg-red-50 p-5">
                  <h3 className="mb-3 text-red-900">
                    {pick(AR.reportsTitle, 'What Users Report')}
                  </h3>
                  <ul className="space-y-2 text-sm text-red-800">
                    {pick(AR.reportsItems, [
                      'Real automation rates of 40-60%',
                      'Months of implementation work',
                      'Hidden costs that balloon fast',
                      'Hallucinations and wrong answers',
                    ]).map((item) => (
                      <li key={item} className="flex items-start gap-2">
                        <X className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>

            {/* The Good */}
            <section id="the-good" className="mb-16">
              <h2 className="mb-6 font-serif text-3xl">
                {pick(AR.goodHeading, 'The Good')}{' '}
                <span className="text-lg text-neutral-500">
                  {pick(AR.goodHeadingAside, "(Let's be fair)")}
                </span>
              </h2>

              <div className="prose prose-lg prose-neutral mb-8 max-w-none">
                <p>
                  {pick(
                    AR.goodIntro,
                    "Zendesk isn't all bad. They've built a comprehensive platform with genuine strengths. Here's what they get right:",
                  )}
                </p>
              </div>

              <div className="mb-8 grid gap-4 sm:grid-cols-2">
                {localizedGoodPoints.map((point) => (
                  <div
                    key={point.title}
                    className="rounded-xl border border-green-200 bg-green-50 p-5"
                  >
                    <div className="mb-3 flex items-center gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-green-100">
                        <point.icon className="h-5 w-5 text-green-600" />
                      </div>
                      <h3 className="text-green-900">{point.title}</h3>
                    </div>
                    <p className="text-sm text-green-800">
                      {point.description}
                    </p>
                  </div>
                ))}
              </div>

              <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-6">
                <p className="text-sm text-neutral-600">
                  {pick(
                    AR.creditNote,
                    <>
                      <strong>Credit where it's due:</strong> Zendesk pioneered
                      many customer service innovations and remains a leader in
                      enterprise support. If you're a Fortune 500 with unlimited
                      budget and an army of implementation consultants, their AI
                      agents might work for you. For everyone else? Keep
                      reading.
                    </>,
                  )}
                </p>
              </div>
            </section>

            {/* The Bad */}
            <section id="the-bad" className="mb-16">
              <h2 className="mb-6 font-serif text-3xl">
                {pick(AR.badHeading, 'The Bad')}{' '}
                <span className="text-lg text-neutral-500">
                  {pick(AR.badHeadingAside, "(This is why you're here)")}
                </span>
              </h2>

              <div className="prose prose-lg prose-neutral mb-8 max-w-none">
                <p>
                  {pick(
                    AR.badIntro,
                    "Now for the part that matters. Based on user reviews, community forums, industry analysis, and our own testing, here's what's wrong with Zendesk AI agents:",
                  )}
                </p>
              </div>

              <div className="space-y-4">
                {localizedBadPoints.map((point) => (
                  <div
                    key={point.title}
                    className={cn(
                      'rounded-xl border-2 p-5 transition-all',
                      point.severity === 'high'
                        ? 'border-red-200 bg-red-50'
                        : 'border-amber-200 bg-amber-50',
                    )}
                  >
                    <button
                      className="flex w-full items-start justify-between text-left"
                      onClick={() =>
                        setExpandedBadPoint(
                          expandedBadPoint === point.title ? null : point.title,
                        )
                      }
                    >
                      <div className="flex items-start gap-4">
                        <div
                          className={cn(
                            'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg',
                            point.severity === 'high'
                              ? 'bg-red-100'
                              : 'bg-amber-100',
                          )}
                        >
                          <point.icon
                            className={cn(
                              'h-5 w-5',
                              point.severity === 'high'
                                ? 'text-red-600'
                                : 'text-amber-600',
                            )}
                          />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3
                              className={cn(
                                '',
                                point.severity === 'high'
                                  ? 'text-red-900'
                                  : 'text-amber-900',
                              )}
                            >
                              {point.title}
                            </h3>
                            {point.severity === 'high' && (
                              <span className="rounded-full bg-red-200 px-2 py-0.5 text-xs font-medium text-red-800">
                                {pick(AR.criticalBadge, 'Critical')}
                              </span>
                            )}
                          </div>
                          <p
                            className={cn(
                              'mt-1 text-sm',
                              point.severity === 'high'
                                ? 'text-red-800'
                                : 'text-amber-800',
                            )}
                          >
                            {point.description}
                          </p>
                        </div>
                      </div>
                      <div className="ml-4 shrink-0">
                        {expandedBadPoint === point.title ? (
                          <Minus
                            className={cn(
                              'h-5 w-5',
                              point.severity === 'high'
                                ? 'text-red-500'
                                : 'text-amber-500',
                            )}
                          />
                        ) : (
                          <Plus
                            className={cn(
                              'h-5 w-5',
                              point.severity === 'high'
                                ? 'text-red-500'
                                : 'text-amber-500',
                            )}
                          />
                        )}
                      </div>
                    </button>
                    {expandedBadPoint === point.title && (
                      <div className="mt-4 border-t border-current/10 pt-4">
                        <ul className="space-y-2">
                          {point.details.map((detail, idx) => (
                            <li
                              key={idx}
                              className={cn(
                                'flex items-start gap-2 text-sm',
                                point.severity === 'high'
                                  ? 'text-red-700'
                                  : 'text-amber-700',
                              )}
                            >
                              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                              {detail}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>

            {/* Service Incidents */}
            <section className="mb-16">
              <h3 className="mb-4 text-xl text-neutral-900">
                {pick(AR.incidentsHeading, 'Recent Service Incidents (2025)')}
              </h3>
              <div className="overflow-x-auto rounded-xl border border-neutral-200">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-neutral-200 bg-neutral-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">
                        {pick(AR.incidentsColDate, 'Date')}
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">
                        {pick(AR.incidentsColIssue, 'Issue')}
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">
                        {pick(AR.incidentsColDuration, 'Duration')}
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">
                        {pick(AR.incidentsColImpact, 'Impact')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {localizedIncidents.map((incident, idx) => (
                      <tr
                        key={incident.date}
                        className={
                          idx % 2 === 0 ? 'bg-white' : 'bg-neutral-50/50'
                        }
                      >
                        <td className="px-4 py-3 text-sm font-medium text-neutral-900">
                          {incident.date}
                        </td>
                        <td className="px-4 py-3 text-sm text-neutral-600">
                          {incident.issue}
                        </td>
                        <td className="px-4 py-3 text-sm text-neutral-600">
                          {incident.duration}
                        </td>
                        <td className="px-4 py-3 text-sm text-neutral-600">
                          {incident.impact}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Cost Calculator */}
            <section id="calculator" className="mb-16">
              <h2 className="mb-6 font-serif text-3xl">
                {pick(AR.calculatorHeading, 'Interactive Cost Calculator')}
              </h2>
              <div className="prose prose-lg prose-neutral mb-8 max-w-none">
                <p>
                  {pick(
                    AR.calculatorIntro,
                    "Let's do the math. Adjust the sliders to see what Zendesk AI agents would actually cost your team, and how much you'd save with alternatives.",
                  )}
                </p>
              </div>
              <CostCalculator />
            </section>

            {/* Customer Complaints */}
            <section id="complaints" className="mb-16">
              <h2 className="mb-6 font-serif text-3xl">
                {pick(AR.complaintsHeading, 'What Real Users Are Saying')}
              </h2>

              <div className="prose prose-lg prose-neutral mb-8 max-w-none">
                <p>
                  {pick(
                    AR.complaintsIntro,
                    <>
                      These aren't cherry-picked outliers. Zendesk has a{' '}
                      <strong>1.8/5 rating on Trustpilot</strong>. Here's what
                      customers are experiencing:
                    </>,
                  )}
                </p>
              </div>

              <div className="space-y-4">
                {localizedComplaints.map((complaint, idx) => (
                  <div
                    key={idx}
                    className="rounded-xl border border-neutral-200 bg-white p-6"
                  >
                    <div className="mb-4 flex items-start gap-3">
                      <Quote className="h-8 w-8 shrink-0 text-neutral-300" />
                      <p className="text-lg italic text-neutral-700">
                        {pick(AR.quoteOpen, '"')}
                        {complaint.quote}
                        {pick(AR.quoteClose, '"')}
                      </p>
                    </div>
                    <div className="flex items-center justify-between border-t border-neutral-100 pt-4">
                      <div>
                        <div className="text-sm font-medium text-neutral-900">
                          {complaint.source}
                        </div>
                        <div className="text-xs text-neutral-500">
                          {complaint.context}
                        </div>
                      </div>
                      <RatingStars rating={complaint.rating} />
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 rounded-xl bg-red-100 p-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
                  <div>
                    <h4 className="text-red-900">
                      {pick(AR.patternTitle, 'The Pattern')}
                    </h4>
                    <p className="mt-1 text-sm text-red-800">
                      {pick(
                        AR.patternBody,
                        "Notice the theme? AI that can't help, won't transfer to humans, and leaves customers stuck. This is AI that blocks support rather than augmenting it.",
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Pricing Comparison Table */}
            <section className="mb-16">
              <h3 className="mb-4 text-xl text-neutral-900">
                {pick(AR.pricingHeading, 'Pricing Comparison')}
              </h3>
              <div className="overflow-x-auto rounded-xl border border-neutral-200">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-neutral-200 bg-neutral-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">
                        {pick(AR.pricingColProvider, 'Provider')}
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">
                        {pick(AR.pricingColBase, 'Base Cost')}
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">
                        {pick(AR.pricingColAi, 'AI Cost')}
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">
                        {pick(AR.pricingColNotes, 'Notes')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {localizedPricing.map((row, idx) => (
                      <tr
                        key={row.provider}
                        className={cn(
                          row.highlight
                            ? 'bg-green-50'
                            : idx % 2 === 0
                              ? 'bg-white'
                              : 'bg-neutral-50/50',
                        )}
                      >
                        <td className="px-4 py-3 text-sm font-medium text-neutral-900">
                          {row.provider}
                          {row.highlight && (
                            <span className="ml-2 rounded-full bg-green-200 px-2 py-0.5 text-xs text-green-800">
                              {pick(AR.bestValueBadge, 'Best Value')}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-neutral-600">
                          {row.baseCost}
                        </td>
                        <td className="px-4 py-3 text-sm text-neutral-600">
                          {row.aiCost}
                        </td>
                        <td className="px-4 py-3 text-sm text-neutral-500">
                          {row.notes}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Feature Comparison */}
            <section id="comparison" className="mb-16">
              <h2 className="mb-6 font-serif text-3xl">
                {pick(AR.comparisonHeading, 'Feature Comparison')}
              </h2>

              <div className="overflow-x-auto rounded-xl border border-neutral-200">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-neutral-200 bg-neutral-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">
                        {pick(AR.featureColFeature, 'Feature')}
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-neutral-700">
                        Zendesk
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-green-700">
                        Open
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-neutral-700">
                        Intercom
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-neutral-700">
                        Freshdesk
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {localizedFeatures.map((row, idx) => (
                      <tr
                        key={row.feature}
                        className={
                          idx % 2 === 0 ? 'bg-white' : 'bg-neutral-50/50'
                        }
                      >
                        <td className="px-4 py-3 text-sm font-medium text-neutral-900">
                          {row.feature}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex justify-center">
                            <FeatureCell value={row.zendesk} />
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center bg-green-50/50">
                          <div className="flex justify-center">
                            <FeatureCell value={row.open} />
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex justify-center">
                            <FeatureCell value={row.intercom} />
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex justify-center">
                            <FeatureCell value={row.freshdesk} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 flex items-center justify-center gap-6 text-xs text-neutral-500">
                <div className="flex items-center gap-1">
                  <CheckCircle className="h-4 w-4 text-green-500" />{' '}
                  {pick(AR.legendFull, 'Full Support')}
                </div>
                <div className="flex items-center gap-1">
                  <HelpCircle className="h-4 w-4 text-amber-500" />{' '}
                  {pick(AR.legendPartial, 'Partial')}
                </div>
                <div className="flex items-center gap-1">
                  <XCircle className="h-4 w-4 text-red-400" />{' '}
                  {pick(AR.legendNone, 'Not Available')}
                </div>
              </div>
            </section>

            {/* Verdict */}
            <section id="verdict" className="mb-16">
              <h2 className="mb-6 font-serif text-3xl">
                {pick(AR.verdictHeading, 'Final Verdict')}
              </h2>

              <div className="rounded-2xl border-2 border-neutral-200 bg-white p-6 md:p-8">
                <div className="mb-6 flex items-center justify-between">
                  <h3 className="text-2xl text-neutral-900">
                    {pick(AR.ourRatingHeading, 'Our Rating')}
                  </h3>
                  <div className="flex items-center gap-2">
                    <span className="text-4xl font-bold text-amber-500">
                      2.5
                    </span>
                    <span className="text-neutral-400">/5</span>
                  </div>
                </div>

                <div className="prose prose-neutral max-w-none">
                  <p>
                    {pick(
                      AR.verdictP1,
                      <>
                        <strong>
                          Zendesk AI agents are a premium product at premium
                          prices, without premium results.
                        </strong>
                      </>,
                    )}
                  </p>
                  <p>
                    {pick(
                      AR.verdictP2,
                      'They work best for large enterprises already deeply invested in the Zendesk ecosystem, with dedicated implementation teams and budgets that can absorb the costs. For these organizations, the tight integration and enterprise security features may justify the premium.',
                    )}
                  </p>
                  <p>
                    {pick(
                      AR.verdictP3,
                      'For everyone else (startups, SMBs, and teams that need AI that actually works out of the box), there are better options. Open offers comparable AI capabilities at 50%+ lower cost with no seat fees. Intercom Fin provides better AI performance per independent testing.',
                    )}
                  </p>
                </div>

                <div className="mt-6 grid gap-4 sm:grid-cols-3">
                  <div className="rounded-lg bg-neutral-50 p-4 text-center">
                    <div className="text-2xl font-bold text-red-500">2/5</div>
                    <div className="text-sm text-neutral-600">
                      {pick(AR.scoreValueLabel, 'Value for Money')}
                    </div>
                  </div>
                  <div className="rounded-lg bg-neutral-50 p-4 text-center">
                    <div className="text-2xl font-bold text-amber-500">3/5</div>
                    <div className="text-sm text-neutral-600">
                      {pick(AR.scoreFeaturesLabel, 'Features')}
                    </div>
                  </div>
                  <div className="rounded-lg bg-neutral-50 p-4 text-center">
                    <div className="text-2xl font-bold text-amber-500">
                      2.5/5
                    </div>
                    <div className="text-sm text-neutral-600">
                      {pick(AR.scoreEaseLabel, 'Ease of Use')}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* FAQ */}
            <section id="faq" className="mb-16">
              <h2 className="mb-8 font-serif text-3xl">
                {pick(AR.faqHeading, 'Frequently Asked Questions')}
              </h2>

              <Accordion type="single" collapsible className="w-full">
                {localizedFaqs.map((faq) => (
                  <AccordionItem
                    key={faq.value}
                    value={faq.value}
                    className="border-b border-neutral-200"
                  >
                    <AccordionTrigger className="py-4 text-left font-medium text-neutral-900 hover:no-underline">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="pb-4 leading-relaxed text-neutral-600">
                      {faq.answerNode ?? faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>

            {/* CTA */}
            <div className="rounded-2xl bg-gradient-to-br from-neutral-900 to-neutral-800 p-8 text-center">
              <h2 className="mb-4 text-2xl text-white">
                {pick(
                  AR.ctaHeading,
                  'Want AI that works without the Zendesk price tag?',
                )}
              </h2>
              <p className="mx-auto mb-6 max-w-xl text-neutral-300">
                {pick(
                  AR.ctaBody,
                  'Open delivers 77% automation at $0.99/resolution, with no seat fees and no implementation nightmares. Works with your existing helpdesk.',
                )}
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button
                  asChild
                  size="lg"
                  className="bg-white text-neutral-900 hover:bg-neutral-100"
                >
                  <Link href="/demo">
                    {pick(AR.ctaDemo, 'See Open in Action')}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white"
                >
                  <Link href="/helpdesks/zendesk">
                    {pick(AR.ctaIntegration, 'Zendesk Integration')}
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
